/**
 * ListingAmenities — what the listing states it has, grouped for reading
 * (LET-99).
 *
 * Diamond Architecture: a server component. Names arrive already resolved for
 * the page locale, so there is no client-side lookup and nothing to translate
 * here; the only strings this component owns are the section heading and the
 * per-category group headings, and the group headings come from the
 * `listing.v1.AmenityCategory` label table (`proto-enums/<package>.yaml`, `client_ui`),
 * not from a hand-written map.
 *
 * WHY GROUPED AND ICONED, when the version #6351 deleted was a flat list of
 * ticks: the wire used to carry bare localized strings, so a flat list was the
 * only thing a client COULD render — while the Rails UI that populated the
 * data grouped by category and drew a per-amenity icon. The wire now carries
 * `slug`, `iconName` and `category`, and this is what that buys.
 *
 * `iconName` is a MATERIAL-ICON NAME (`domain`, `fitness_center`), not a storage
 * key and not a lucide name, so it is mapped — by `amenityIcon`, which lives in
 * `@letsgethome/ui` beside the owner's `AmenityPicker` rather than here. ONE
 * glyph table, deliberately: the same amenity must not be drawn with one icon
 * where an owner ticks it and another where a searcher reads it, and two
 * private copies of a map drift the first time either is edited. The unmapped
 * name is not an error there either — a vocabulary row added by staff renders
 * with the neutral tick, which is exactly what the flat list did for every row.
 */
import { useLocale, useTranslations } from '@/shared/i18n';
import { AMENITY_CATEGORY_ORDER } from '@/shared/lib/listings/amenity-categories';
import type { ListingAmenityData } from '@/shared/lib/types/listing-detail';
import { type ProtoEnumLocale } from '@letsgethome/lib/proto-enums/common/labels_gen';
import { amenityCategoryLabel } from '@letsgethome/lib/proto-enums/listing/labels_gen';
import { plaintextString } from '@letsgethome/types/branded';
import { amenityIcon } from '@letsgethome/ui/amenity-picker';

type Props = {
  /** In the server's order (category, then English name). */
  amenities: readonly ListingAmenityData[];
};

export function ListingAmenities({ amenities }: Props) {
  const t = useTranslations('listings.detail');
  const locale = useLocale() as ProtoEnumLocale;

  if (amenities.length === 0) {
    return null;
  }

  const groups = AMENITY_CATEGORY_ORDER.map((category) => ({
    category,
    items: amenities.filter((a) => a.category === category),
  })).filter((g) => g.items.length > 0);

  if (groups.length === 0) {
    return null;
  }

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">{t('amenities')}</h2>
      {groups.map((group) => (
        <div key={group.category} className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">
            {amenityCategoryLabel(group.category, locale)}
          </h3>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {group.items.map((amenity) => {
              const Icon = amenityIcon(amenity.iconName);
              return (
                <li key={amenity.slug} className="flex items-center gap-2 text-muted-foreground">
                  <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-success-text" />
                  <span>{plaintextString(amenity.name)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}
